"use client";

import {CurriculumTransitionNotice,CS_TRANSITION_URL} from '@/app/Components/AcademicWiki/CurriculumTransitionNotice';
import Link from "next/link";
import type { ReactNode } from "react";
import { readableCourseTitle } from "@/app/lib/courseTitle";
import { IntensityPips, QualityBar, NoDataPill, InfoTip, intensityWord } from '@/app/Components/AcademicWiki/primitives';
import { useUiPreferences } from "@/app/Components/UiPreferencesProvider";
import { getDepartmentAcademicSwatch } from "@/app/lib/academicPalette";
import {
  OperationalMetric,
  OperationalPage,
  OperationalResult,
  OperationalSection,
  OperationalSource,
  OperationalState,
  OperationalStatus,
  Surface,
} from "@/app/Components/OperationalPage";
import { buttonClassName } from "@/app/Components/buttonStyles";
import CourseWikiRichContent from "@/app/courses/CourseWikiRichContent";
import ProfessorLink from "@/app/courses/ProfessorLink";
import { RUTGERS_SCHOOL_CODES_SOURCE_URL } from "@/app/lib/rutgersSchoolCodes";
import CourseGradeEvidence from "@/app/explore/CourseGradeEvidence";
import {
  formatCoursePercent,
  formatCourseScore,
  type CourseDetailViewModel,
} from "@/app/courses/courseDetailViewModel";
import type { ProfessorLinkPreview } from "@/app/professors/professorProfileContent";
import RoomLocationLink from "@/app/Components/RoomLocationLink";
import { EntityShareButton } from "@/app/Components/EntityShareButton";
import { isSemesterId } from "@/app/lib/semesters";

export function CourseDetailView({ model }: { model: CourseDetailViewModel }) {
  const {
    profile,
    wiki,
    requirementLists,
    professorPreviews,
    latestTerm,
    latestSections,
    openSections,
    campuses,
    instructors,
    sniperIndex,
    loadedTerms,
    assistantPrompt,
  } = model;
  // A registration index is meaningful only within the snapshot that supplied it.
  const watchSection = latestSections.find(section => section.registrationIndex === sniperIndex);
  const watchSemester = isSemesterId(watchSection?.semesterKey) ? watchSection.semesterKey : null;
  const offeringSemester = isSemesterId(latestSections[0]?.semesterKey) ? latestSections[0].semesterKey : null;
  const hasIntroTransition=['01198111','01198112'].includes(profile.baseId);
  const { academicPalettePreferences, academicPalettePresets } = useUiPreferences();
  const swatch = getDepartmentAcademicSwatch(profile.subject, academicPalettePreferences.coursePaletteId, academicPalettePresets);
  const difficulty = wiki?.courseRatings?.difficulty.score;
  const weeklyHours = wiki?.research?.data.atAGlance?.weeklyHours;
  const assessment = wiki?.research?.data.assessment;
  const schoolPrerequisites = model.prerequisites?.filter(course => course.id.startsWith(profile.school)).slice(0, 3) ?? [];
  const patterns = meetingPatterns(latestSections);
  // Each cell answers one question a student asks before registering; cells without evidence are left out.
  const glance: Array<{ label: string; value: ReactNode; detail?: string }> = [
    ...(hasIntroTransition ? [{ label: 'Curriculum transition', value: <a href={CS_TRANSITION_URL} className="text-focus underline">Spring 2027 →</a>, detail: 'CS 120/121/122 replace this course' }] : []),
    ...(schoolPrerequisites.length ? [{ label: 'Can I take it?', value: <span className="flex flex-wrap items-center gap-x-2 gap-y-1">{schoolPrerequisites.map(course => <Link key={course.id} href={`/courses/${course.id}`} title={course.title} className="font-mono text-sm text-[var(--ss-focus)] hover:underline">{course.id.slice(2, 5)}:{course.id.slice(5)}</Link>)}<InfoTip title="Prerequisite alternatives" text={(profile.prerequisiteNotes ?? 'Check catalog requirements').replace(/<[^>]+>/g, ' ')} /></span>, detail: schoolPrerequisites.map(course => course.title).join(' · ') }]
      : profile.prerequisiteNotes ? [{ label: 'Can I take it?', value: profile.prerequisiteNotes.replace(/<[^>]+>/g, ' ').trim() }]
      : profile.sections.length ? [{ label: 'Can I take it?', value: 'No prerequisites listed', detail: 'Rutgers Schedule of Classes' }] : []),
    ...(difficulty != null ? [{ label: 'How hard?', value: <span className="flex items-center gap-2">{difficulty}/5 · {intensityWord(difficulty)}<IntensityPips score={difficulty} label="Difficulty" /></span>, detail: weeklyHours ? `${weeklyHours.low}–${weeklyHours.high} hrs/week outside class` : undefined }]
      : weeklyHours ? [{ label: 'Weekly effort', value: `${weeklyHours.low}–${weeklyHours.high} hrs/week`, detail: weeklyHours.basis }] : []),
    ...(assessment?.components.length ? [{ label: 'How it’s graded', value: assessment.components.slice(0, 4).map(item => item.weightPct != null ? `${item.component} ${item.weightPct}%` : item.component).join(' · '), detail: assessment.term }] : []),
    ...(profile.sirs?.avgTeachingEffectiveness != null ? [{ label: 'Students rate teaching', value: `${profile.sirs.avgTeachingEffectiveness.toFixed(2)} / 5`, detail: `SIRS · ${profile.sirs.totalResponses.toLocaleString()} responses${profile.sirs.firstYear && profile.sirs.lastYear ? ` · ${profile.sirs.firstYear}–${profile.sirs.lastYear}` : ''}` }] : []),
    ...(latestSections.length ? [{ label: latestTerm ?? 'Section snapshot', value: `${latestSections.length} section${latestSections.length === 1 ? '' : 's'}`, detail: [`${openSections.length} open in snapshot`, instructors.length ? `${instructors.length} instructor${instructors.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ') }] : profile.announcement ? [{ label: 'Announced start', value: profile.announcement.effectiveTerm }] : []),
    ...(patterns.length ? [{ label: 'When it meets', value: patterns.slice(0, 2).join(' · '), detail: campuses.length ? campuses.join(', ') : undefined }] : []),
  ];
  return (
    <OperationalPage className="mx-auto max-w-7xl px-4 pb-8 pt-4 sm:px-6 lg:px-8">
      <nav
        aria-label="Breadcrumb"
        className="mb-4 text-sm text-[var(--ss-text-muted)]"
      >
        <Link href="/courses" className={buttonClassName({ variant: "link" })}>
          Course Wiki
        </Link>
        <span className="mx-2" aria-hidden="true">
          /
        </span>
        <span className="font-mono" aria-current="page">{profile.displayId}</span>
      </nav>
      <header className="max-w-5xl">
        <p className="font-mono text-xs text-[var(--ss-text-muted)]">
          <span className="font-semibold text-[var(--ss-text)]">{profile.displayId}</span>
          {' · '}{profile.departmentName ? readableCourseTitle(profile.departmentName) : `Subject ${profile.subject}`}
          {' · '}<a href={RUTGERS_SCHOOL_CODES_SOURCE_URL} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">{profile.schoolLabel}</a>
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
          <span className="mr-2 inline-block h-3 w-3 rounded-full align-middle" style={{ background: swatch.border }} aria-hidden="true" />
          {readableCourseTitle(profile.fullTitle)}
        </h1>
        {wiki ? <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--ss-text-muted)]">{wiki.officialSummary}</p> : null}
        <dl aria-label="Course facts" className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          {Number(profile.credits) > 0 ? <div className="course-fact-chip"><dd className="font-semibold">{profile.credits}</dd><dt>credits</dt></div> : null}
          {profile.offeredTerms.length ? <div className="course-fact-chip"><dt className="sr-only">Typical seasons</dt><dd>{profile.offeredTerms.join(' · ')}</dd></div> : null}
          {profile.coreCodes.length ? <div className="course-fact-chip"><dt>Core</dt><dd className="font-semibold">{profile.coreCodes.join(', ')}</dd></div> : null}
          {requirementLists.length ? <div className="course-fact-chip"><dt className="sr-only">Degree requirements</dt><dd>In {requirementLists.length} requirement list{requirementLists.length === 1 ? '' : 's'}</dd></div> : null}
          {wiki ? <div className="course-fact-chip"><dt className="sr-only">Guide</dt><dd><a href="#guide" className="hover:underline">{wiki.pilot ? 'Unreviewed pilot candidate' : 'Researched guide'} · {wiki.sourceLinks.length} source{wiki.sourceLinks.length === 1 ? '' : 's'}</a></dd></div> : null}
          {profile.announcement ? <div className="course-fact-chip"><dt className="sr-only">Status</dt><dd>Officially announced course</dd></div> : null}
        </dl>
      </header>
      {profile.announcement?<CurriculumTransitionNotice effectiveTerm={profile.announcement.effectiveTerm} summary="New introductory sequence · Spring 2027 →" description="CS120/121/122 form the announced introductory sequence. CS111 stops being offered in Spring 2027 and CS112 in Fall 2027; consult the department's dated equivalency and repeat-course rules." href={profile.announcement.sourceUrl} relatedCourses={[{href:'/courses/01198111',label:'198:111 Introduction to Computer Science'},{href:'/courses/01198112',label:'198:112 Data Structures'}]}/>:null}{hasIntroTransition?<CurriculumTransitionNotice effectiveTerm="Spring 2027" summary="Being replaced by CS 120/121/122 starting Spring 2027 — see what this means for you →" description="CS111 stops being offered Spring2027; CS112 stops Fall2027. Repeats and transfer equivalencies have different term-specific rules. Check the department announcement with your adviser before planning the replacement sequence."/>:null}
      <section aria-label="At a glance" className="course-glance mt-4">
        {glance.map(cell => <div key={cell.label} className="course-glance-cell"><p className="text-xs text-[var(--ss-text-muted)]">{cell.label}</p><div className="mt-0.5 text-base font-semibold leading-6">{cell.value}</div>{cell.detail ? <p className="mt-0.5 text-xs leading-5 text-[var(--ss-text-muted)]">{cell.detail}</p> : null}</div>)}
      </section>
      {wiki?.courseRatings && Object.values(wiki.courseRatings).filter(signal=>signal.score!=null).length <= 3 ? <div className="mt-3 flex flex-wrap gap-3">{Object.entries(wiki.courseRatings).filter(([,signal])=>signal.score!=null).map(([name,signal])=><OperationalStatus key={name} tone="warning"><span className="capitalize">{name.replace(/([A-Z])/g,' $1')}</span> {signal.score}/5 · {intensityWord(signal.score!)} <IntensityPips score={signal.score!} label={name}/><InfoTip title={name} text={`${signal.reason} · ${signal.confidence} confidence`}/></OperationalStatus>)}</div>:null}
      <div className="mt-4 flex flex-wrap gap-2">
        {profile.sections.length ? <>
        {offeringSemester ? <>
        <Link href={`/scheduling?add=${encodeURIComponent(profile.baseId)}&semester=${offeringSemester}`} className={buttonClassName({ variant: "primary" })}>Add to schedule</Link>
        <Link href={`/explore?q=${encodeURIComponent(profile.displayId)}&semester=${offeringSemester}`} className={buttonClassName({ variant: "secondary" })}>Compare in Explorer</Link>
        </> : <span className="text-sm text-[var(--ss-text-muted)]">No supported section term is loaded for these handoffs.</span>}
        <Link href={sniperIndex && watchSemester ? `/scheduling/alerts?course=${encodeURIComponent(sniperIndex)}&semester=${watchSemester}` : "/course-sniper"} className={buttonClassName({ variant: "secondary" })}>Watch openings</Link>
        </> : <Link href={`/degree-planning?addCourse=${encodeURIComponent(profile.displayId)}`} className={buttonClassName({variant:'primary'})}>Plan with this course</Link>}
        <Link href={`/ai-assistant?prompt=${encodeURIComponent(assistantPrompt)}&autosend=1&handoff=course-${profile.baseId}`} className={buttonClassName({ variant: "ai" })}>Ask Scarlet Sync</Link>
        <EntityShareButton label="Share course" title={`${profile.displayId} ${profile.fullTitle}`} text={`${profile.displayId} — ${profile.fullTitle}\nRutgers University`} url={`https://scarletsync.app/courses/${profile.baseId}`} />
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-5">
          {instructors.length?<OperationalSection title={`Who’s teaching it — ${latestTerm ?? 'no term loaded'}`} className="border-l-[3px] border-l-[var(--ss-positive)]" description="Checked-in sections · current seat counts are unavailable.">
            <div className="divide-y divide-line">{instructors.map(name => ({name, sections: latestSections.filter(section => section.instructors.includes(name))})).sort((a,b) => b.sections.length - a.sections.length).map(({name,sections}) => { const survey=profile.sirs?.topInstructors.find(row => row.name.toLowerCase().replace(/[^a-z0-9]/g, '') === name.toLowerCase().replace(/[^a-z0-9]/g, '')); return <div key={name} className="flex flex-wrap items-center gap-3 py-4"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--ss-positive-bg)] text-sm font-semibold text-[var(--ss-positive)]" aria-hidden="true">{name.split(/[, ]+/).filter(Boolean).map(word=>word[0]).slice(0,2).join('')}</span><div className="min-w-0 flex-1"><ProfessorLink preview={previewFor(name, professorPreviews)} />{!name.includes(',') && !name.includes(' ') ? <p className="text-xs text-content-muted">First name not listed in schedule</p>:null}<p className="mt-1 text-xs text-content-muted">{[meetingPatterns(sections)[0], [...new Set(sections.map(section => section.campus))].join(', '), `${sections.length} section${sections.length === 1 ? '' : 's'}`, `${sections.filter(section => section.open).length} open`].filter(Boolean).join(' · ')}</p></div><div className="w-full sm:w-64">{survey?.avgTeachingEffectiveness != null ? <QualityBar value={survey.avgTeachingEffectiveness} label="Course SIRS teaching" detail={`Historical survey mean for this instructor and course, ${survey.offerings} offerings. Course quality ${formatCourseScore(survey.avgOverallQuality)}. Identity is matched to the full observed name; this is not an AI rating.`}/> : <NoDataPill>No course SIRS matched yet</NoDataPill>}</div></div>; })}</div>
          </OperationalSection>:null}
          {wiki ? (
            <OperationalSection
              title={wiki.pilot ? "Pilot research candidate" : "Course guide"}
              description="AI-generated overview"
              actions={
                <a href="#sources" className={buttonClassName({ variant: "subtle" })}>
                  Sources ({wiki.sourceLinks.length})
                </a>
              }
            >
              <div id="guide">
                <CourseWikiRichContent wiki={wiki} />
              </div>
            </OperationalSection>
          ) : null}
          {!profile.announcement?<>
          <OperationalSection
            title={latestTerm ? `${latestTerm} sections` : "No loaded sections"}
            description={
              latestTerm
                ? `${openSections.length} open · ${campuses.length ? campuses.join(", ") : "campus not listed"}`
                : undefined
            }
          >
            <div id="sections">
              {latestSections.length ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full min-w-[44rem] text-left text-sm">
                    <thead className="border-b border-[var(--ss-border)] text-xs uppercase tracking-wide text-[var(--ss-text-muted)]">
                      <tr>
                        <th scope="col" className="py-3 pr-3">
                          Section
                        </th>
                        <th scope="col" className="py-3 pr-3">
                          Status
                        </th>
                        <th scope="col" className="py-3 pr-3">
                          Instructor
                        </th>
                        <th scope="col" className="py-3 pr-3">
                          Meeting
                        </th>
                        <th scope="col" className="py-3">
                          Campus
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--ss-border)]">
                      {latestSections.slice(0, 16).map((section) => (
                        <tr
                          key={`${section.semesterKey}-${section.registrationIndex}-${section.sectionNumber}`}
                        >
                          <td className="py-3 pr-3">
                            <span className="font-semibold">
                              {section.sectionNumber}
                            </span>
                            <span className="ml-2 font-mono text-xs text-[var(--ss-text-muted)]">
                              {section.registrationIndex}
                            </span>
                          </td>
                          <td className="py-3 pr-3">
                            <OperationalStatus
                              tone={section.open ? "positive" : "neutral"}
                            >
                              {section.open ? "Open" : "Closed"}
                            </OperationalStatus>
                          </td>
                          <td className="py-3 pr-3">
                            <ProfessorLinks
                              names={section.instructors}
                              previews={professorPreviews}
                            />
                          </td>
                          <td className="py-3 pr-3 text-[var(--ss-text-muted)]">
                            <span>{section.meetingSummary}</span>
                            {section.rooms?.length ? (
                              <span className="mt-1 flex flex-wrap gap-2">
                                {section.rooms.map((room) => (
                                  <RoomLocationLink
                                    key={room}
                                    location={room}
                                    className="text-xs font-semibold"
                                  />
                                ))}
                              </span>
                            ) : null}
                          </td>
                          <td className="py-3">{section.campus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-4">
                  <OperationalState
                    kind="empty"
                    title="No section snapshot is loaded for this course"
                    description="The catalog identity is available, but the checked-in term datasets contain no section records."
                    action={
                      <Link
                        href={`/explore?q=${encodeURIComponent(profile.displayId)}`}
                        className="font-semibold text-[var(--ss-focus)] hover:underline"
                      >
                        Check Explorer
                      </Link>
                    }
                  />
                </div>
              )}
              <div className="mt-4">
                <OperationalSource
                  source="Checked-in Rutgers Schedule of Classes snapshots"
                  updatedAt="when term datasets are refreshed"
                  stale
                />
              </div>
            </div>
          </OperationalSection>
          <OperationalSection
            title="SIRS teaching signals"
            description="Historical student surveys"
            actions={
              <Link
                href={`/sirs?course=${encodeURIComponent(`${profile.subject}:${profile.number}`)}`}
                className={buttonClassName({ variant: "secondary" })}
              >
                Open full SIRS search
              </Link>
            }
          >
            <div id="teaching">
              {profile.sirs ? (
                <>
                  <div className="mt-4 grid gap-3 sm:grid-cols-4">
                    <OperationalMetric
                      label="Teaching"
                      value={formatCourseScore(
                        profile.sirs.avgTeachingEffectiveness,
                      )}
                    />
                    <OperationalMetric
                      label="Course quality"
                      value={formatCourseScore(profile.sirs.avgOverallQuality)}
                    />
                    <OperationalMetric
                      label="Response rate"
                      value={formatCoursePercent(profile.sirs.responseRate)}
                    />
                    <OperationalMetric
                      label="Coverage"
                      value={`${profile.sirs.totalOfferings} offerings · ${profile.sirs.firstYear ?? "—"}–${profile.sirs.lastYear ?? "—"}`}
                    />
                  </div>
                  {profile.sirs.topInstructors.length ? (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full min-w-[36rem] text-left text-sm">
                        <thead className="border-b border-[var(--ss-border)] text-xs uppercase tracking-wide text-[var(--ss-text-muted)]">
                          <tr>
                            <th scope="col" className="py-3">
                              Instructor
                            </th>
                            <th scope="col">Offerings</th>
                            <th scope="col">Teaching</th>
                            <th scope="col">Quality</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[var(--ss-border)]">
                          {profile.sirs.topInstructors.map((instructor) => (
                            <tr key={instructor.name}>
                              <td className="py-3">
                                <ProfessorLink
                                  preview={previewFor(
                                    instructor.name,
                                    professorPreviews,
                                  )}
                                />
                              </td>
                              <td>{instructor.offerings}</td>
                              <td>
                                {formatCourseScore(
                                  instructor.avgTeachingEffectiveness,
                                )}
                              </td>
                              <td>
                                {formatCourseScore(
                                  instructor.avgOverallQuality,
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="mt-4">
                  <OperationalState
                    kind="empty"
                    title="No SIRS summary is connected to this course"
                    description="This absence does not mean the course has never been surveyed; it describes current checked-in coverage."
                  />
                </div>
              )}
            </div>
          </OperationalSection>
          <OperationalSection
            title="Course stats"
            description="Catalog and planning context"
          >
            <div id="stats">
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <OperationalMetric label="Credits" value={profile.credits} />
                <OperationalMetric
                  label="Current campuses"
                  value={campuses.length ? campuses.join(", ") : profile.campus}
                />
                <OperationalMetric
                  label="Current availability"
                  value={
                    latestTerm
                      ? `${openSections.length} open of ${latestSections.length}`
                      : "No snapshot"
                  }
                  tone={openSections.length ? "positive" : undefined}
                />
                <OperationalMetric
                  label="Catalog terms"
                  value={
                    profile.offeredTerms.length
                      ? profile.offeredTerms.join(", ")
                      : "Not listed"
                  }
                />
                <OperationalMetric
                  label="Core codes"
                  value={
                    profile.coreCodes.length
                      ? profile.coreCodes.join(", ")
                      : "None listed"
                  }
                />
                <OperationalMetric
                  label="Loaded terms"
                  value={String(loadedTerms)}
                />
              </div>
              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <Surface className="p-4">
                  <h3 className="font-semibold text-[var(--ss-text)]">
                    Prerequisites
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-[var(--ss-text-muted)]">
                    {profile.prerequisiteNotes ||
                      "No prerequisite note appears in the checked-in Rutgers catalog snapshots."}
                  </p>
                </Surface>
                <Surface className="p-4">
                  <h3 className="font-semibold text-[var(--ss-text)]">
                    Degree requirement lists
                  </h3>
                  {requirementLists.length ? (
                    <>
                      <ul className="mt-2 space-y-2 text-sm text-[var(--ss-text-muted)]">
                        {requirementLists.slice(0, 8).map((list) => (
                          <li key={list.key}>{list.label}</li>
                        ))}
                      </ul>
                      {requirementLists.length > 8 ? (
                        <details className="mt-3">
                          <summary
                            className={buttonClassName({ variant: "link" })}
                          >
                            Show {requirementLists.length - 8} more lists
                          </summary>
                          <ul className="mt-2 space-y-2 text-sm text-[var(--ss-text-muted)]">
                            {requirementLists.slice(8).map((list) => (
                              <li key={list.key}>{list.label}</li>
                            ))}
                          </ul>
                        </details>
                      ) : null}
                    </>
                  ) : (
                    <p className="mt-2 text-sm text-[var(--ss-text-muted)]">
                      No direct degree-list membership appears in the checked-in
                      requirement index.
                    </p>
                  )}
                </Surface>
              </div>
              <div className="mt-4">
                <CourseGradeEvidence baseId={profile.baseId} />
              </div>
            </div>
          </OperationalSection>
          </>:null}
          <OperationalSection
            title={`Related ${profile.departmentName ?? "subject"} courses`}
          >
            <div id="related">
              {profile.relatedCourses.length ? (
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {profile.relatedCourses.map((course) => (
                    <OperationalResult
                      key={course.baseId}
                      actions={
                        <Link
                          href={`/courses/${course.baseId}`}
                          className={buttonClassName({ variant: "secondary" })}
                        >
                          Open course
                        </Link>
                      }
                    >
                      <p className="font-mono text-xs text-[var(--ss-text-muted)]">
                        {course.displayId}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-[var(--ss-text)]">
                        {course.title}
                      </p>
                    </OperationalResult>
                  ))}
                </div>
              ) : (
                <OperationalState
                  kind="empty"
                  title="No related course records are indexed"
                />
              )}
            </div>
          </OperationalSection>
        </div>
        <aside
          className="space-y-4 lg:sticky lg:top-24 lg:h-fit"
          aria-label="Course record context"
        >
          <Surface className="p-4">
            <nav aria-label="On this page">
              <h2 className="operational-section-title">On this page</h2>
              {[
                ...(wiki ? [["Course guide", "#guide"]] : []),
                ...(!profile.announcement?[["Sections", "#sections"],["SIRS", "#teaching"],["Course stats", "#stats"]]:[]),
                ["Related courses", "#related"],
                ...(wiki ? [["Sources", "#sources"]] : []),
              ].map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  className={buttonClassName({
                    variant: "subtle",
                    className: "w-full justify-start",
                  })}
                >
                  {label}
                </a>
              ))}
            </nav>
          </Surface>
        </aside>
      </div>
    </OperationalPage>
  );
}

function previewFor(
  name: string,
  previews: CourseDetailViewModel["professorPreviews"],
): ProfessorLinkPreview {
  return (
    (previews[name] ? { ...previews[name], displayName: displayInstructorName(previews[name].displayName) } : null) ?? {
      href: `/sirs?professor=${encodeURIComponent(name)}`,
      canonical: false,
      displayName: displayInstructorName(name),
      summary:
        "A canonical Professor Wiki profile is not available for this instructor yet.",
      meta: "Open matching SIRS records",
    }
  );
}

function ProfessorLinks({
  names,
  previews,
}: {
  names: string[];
  previews: CourseDetailViewModel["professorPreviews"];
}) {
  return (
    <>
      {names.map((name, index) => (
        <span key={`${name}-${index}`}>
          {index ? ", " : null}
          {/^tba$/i.test(name) ? (
            name
          ) : (
            <ProfessorLink preview={previewFor(name, previews)} />
          )}
        </span>
      ))}
    </>
  );
}

function displayInstructorName(name:string) {const parts=name.split(',').map(part=>part.trim());const friendly=parts.length===2?`${parts[1]} ${parts[0]}`:name;return friendly.toLowerCase().replace(/\b[a-z]/g,letter=>letter.toUpperCase());}

const DAY_ORDER = 'MTWHFSU';
const DAY_LABEL: Record<string, string> = { H: 'Th', S: 'Sa', U: 'Su' };
const FULL_DAY: Record<string, string> = { monday: 'M', tuesday: 'T', wednesday: 'W', thursday: 'H', friday: 'F', saturday: 'S', sunday: 'U' };

/** The most common lecture times across sections, e.g. "MW 10:20 AM". */
export function meetingPatterns(sections: CourseDetailViewModel['latestSections']): string[] {
  const counts = new Map<string, number>();
  for (const section of sections) {
    const byStart = new Map<string, string[]>();
    for (const part of section.meetingSummary.split(';')) {
      const match = part.trim().match(/^([A-Za-z]+) (\d{1,2}:\d{2} [AP]M)-/);
      if (!match) continue;
      // Term datasets spell days either as letters ("MW", H = Thursday) or in full ("Tuesday").
      const days = FULL_DAY[match[1].toLowerCase()] ?? (/^[MTWHFSU]+$/.test(match[1]) ? match[1] : '');
      if (days) byStart.set(match[2], [...(byStart.get(match[2]) ?? []), ...days.split('')]);
    }
    const lecture = [...byStart.entries()].sort((a, b) => b[1].length - a[1].length)[0];
    if (!lecture) continue;
    const days = [...new Set(lecture[1])].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)).map(day => DAY_LABEL[day] ?? day).join('');
    const key = `${days} ${lecture[0]}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([key]) => key);
}
